import { useEffect, useState } from "react";
import { api, formatRupiah, currentYM, dispDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Eye, CheckCircle2, AlertCircle, X, Download } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

const STATUS_MAP = {
  active: ["bg-blue-100 text-blue-800 border-blue-300", "Berjalan"],
  paid_off: ["bg-green-100 text-green-800 border-green-300", "Lunas"],
  cancelled: ["bg-zinc-100 text-zinc-700 border-zinc-300", "Dibatalkan"],
};

const StatusBadge = ({ status }) => {
  const [cls, label] = STATUS_MAP[status] || ["", status];
  return <Badge className={`${cls} border`} variant="outline">{label}</Badge>;
};

export default function AdminLoans() {
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto" data-testid="admin-loans-page">
      <p className="overline">HRD</p>
      <h1 className="font-display text-3xl font-bold tracking-tight">Pinjaman Pegawai</h1>
      <p className="text-sm text-zinc-500 mt-1">
        Kelola pinjaman jangka panjang dengan cicilan otomatis dipotong dari slip gaji bulanan.
      </p>

      <Tabs defaultValue="loans" className="mt-6">
        <TabsList>
          <TabsTrigger value="loans" data-testid="tab-loans">Daftar Pinjaman</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Kategori</TabsTrigger>
        </TabsList>
        <TabsContent value="loans" className="mt-4">
          <LoansSection />
        </TabsContent>
        <TabsContent value="categories" className="mt-4">
          <CategoriesSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ============ KATEGORI ============ */
const CategoriesSection = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/loan-categories")
      .then((r) => setList(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/loan-categories/${editing.id}`, { name });
        toast.success("Kategori diperbarui");
      } else {
        await api.post("/loan-categories", { name });
        toast.success("Kategori ditambah");
      }
      setName("");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setBusy(false);
    }
  };

  const del = async (cat) => {
    if (!window.confirm(`Hapus kategori "${cat.name}"?`)) return;
    try {
      await api.delete(`/loan-categories/${cat.id}`);
      toast.success("Kategori dihapus");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal hapus");
    }
  };

  if (loading) return <PageLoader label="Memuat kategori..." />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card className="p-5 md:col-span-1 border border-zinc-200 shadow-none">
        <p className="overline mb-1">{editing ? "Edit Kategori" : "Tambah Kategori"}</p>
        <h3 className="font-display text-lg font-semibold mb-3">Master Kategori</h3>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Nama Kategori</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Contoh: Kendaraan, Elektronik"
              data-testid="input-category-name"
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !name.trim()} className="flex-1" data-testid="btn-save-category">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Update" : "Tambah")}
            </Button>
            {editing && (
              <Button type="button" variant="outline" onClick={() => { setEditing(null); setName(""); }}>
                Batal
              </Button>
            )}
          </div>
        </form>
      </Card>

      <Card className="p-5 md:col-span-2 border border-zinc-200 shadow-none">
        <p className="overline mb-3">{list.length} kategori terdaftar</p>
        {list.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-8">Belum ada kategori</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama Kategori</TableHead>
                <TableHead className="w-32 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((cat) => (
                <TableRow key={cat.id} data-testid={`cat-row-${cat.id}`}>
                  <TableCell className="font-medium">{cat.name}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(cat); setName(cat.name); }} data-testid={`btn-edit-cat-${cat.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(cat)} className="text-red-600" data-testid={`btn-del-cat-${cat.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};

/* ============ DAFTAR PINJAMAN ============ */
const emptyForm = {
  user_id: "",
  category_id: "",
  title: "",
  description: "",
  principal: "",
  monthly_minimum: "500000",
  start_period: currentYM(),
  note: "",
};

const LoansSection = () => {
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("all");
  const [filterStatus, setFilterStatus] = useState("active");
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editLoan, setEditLoan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detailLoan, setDetailLoan] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const exportXlsx = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (filterUser !== "all") params.user_id = filterUser;
      if (filterStatus !== "all") params.status = filterStatus;
      const res = await api.get("/loans/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rekap-pinjaman-${filterStatus}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel berhasil diunduh");
    } catch {
      toast.error("Gagal mengunduh Excel");
    } finally {
      setDownloading(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterUser !== "all") params.user_id = filterUser;
      if (filterStatus !== "all") params.status = filterStatus;
      const [lRes, uRes, cRes] = await Promise.all([
        api.get("/loans", { params }),
        api.get("/users"),
        api.get("/loan-categories"),
      ]);
      setList(lRes.data);
      setUsers(uRes.data.filter((u) => u.role === "pegawai"));
      setCats(cRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [filterUser, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const openForm = (loan = null) => {
    if (loan) {
      setEditLoan(loan);
      setForm({
        user_id: loan.user_id,
        category_id: loan.category_id,
        title: loan.title,
        description: loan.description || "",
        principal: String(loan.principal),
        monthly_minimum: String(loan.monthly_minimum),
        start_period: loan.start_period,
        note: loan.note || "",
      });
    } else {
      setEditLoan(null);
      setForm(emptyForm);
    }
    setOpenCreate(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.category_id || !form.title || !form.principal) {
      toast.error("Lengkapi semua field wajib");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        user_id: form.user_id,
        category_id: form.category_id,
        title: form.title.trim(),
        description: form.description.trim(),
        principal: parseFloat(form.principal),
        monthly_minimum: parseFloat(form.monthly_minimum),
        start_period: form.start_period,
        note: form.note.trim(),
      };
      if (editLoan) {
        await api.put(`/loans/${editLoan.id}`, payload);
        toast.success("Pinjaman diperbarui");
      } else {
        await api.post("/loans", payload);
        toast.success("Pinjaman ditambah");
      }
      setOpenCreate(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setBusy(false);
    }
  };

  const del = async (loan) => {
    if (!window.confirm(`Hapus pinjaman "${loan.title}" milik ${loan.user_name}? Semua histori cicilan juga akan terhapus.`)) return;
    try {
      await api.delete(`/loans/${loan.id}`);
      toast.success("Pinjaman dihapus");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal hapus");
    }
  };

  if (loading) return <PageLoader label="Memuat pinjaman..." />;

  const totalPrincipal = list.reduce((s, l) => s + l.principal, 0);
  const totalOutstanding = list.reduce((s, l) => s + l.outstanding, 0);

  return (
    <div className="space-y-4" data-testid="loans-section">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 border border-zinc-200 shadow-none">
          <p className="overline mb-1 text-[0.6rem]">Total Pinjaman</p>
          <p className="font-display text-xl font-bold tracking-tight">{list.length}</p>
          <p className="text-xs text-zinc-500 mt-1">{filterStatus === "all" ? "semua status" : STATUS_MAP[filterStatus]?.[1]}</p>
        </Card>
        <Card className="p-4 border border-zinc-200 shadow-none">
          <p className="overline mb-1 text-[0.6rem]">Total Pokok</p>
          <p className="font-display text-lg sm:text-xl font-bold tracking-tight" data-testid="total-principal">{formatRupiah(totalPrincipal)}</p>
        </Card>
        <Card className="p-4 border border-zinc-200 shadow-none">
          <p className="overline mb-1 text-[0.6rem]">Total Sisa Hutang</p>
          <p className="font-display text-lg sm:text-xl font-bold tracking-tight text-amber-700" data-testid="total-outstanding">{formatRupiah(totalOutstanding)}</p>
        </Card>
      </div>

      {/* Filters + Add */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
        <div className="flex-1">
          <Label className="text-xs">Filter Pegawai</Label>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger data-testid="filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Pegawai</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} ({u.employee_id})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label className="text-xs">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="active">Berjalan</SelectItem>
              <SelectItem value="paid_off">Lunas</SelectItem>
              <SelectItem value="cancelled">Dibatalkan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => openForm()} data-testid="btn-add-loan">
          <Plus className="w-4 h-4 mr-1" /> Tambah Pinjaman
        </Button>
        <Button
          onClick={exportXlsx}
          disabled={downloading || list.length === 0}
          variant="outline"
          className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
          data-testid="btn-export-loans-xlsx"
        >
          {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
          Excel
        </Button>
      </div>

      {/* List */}
      {list.length === 0 ? (
        <Card className="p-12 text-center border border-zinc-200 shadow-none">
          <p className="text-sm text-zinc-500">Belum ada pinjaman dengan filter ini</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {list.map((loan) => (
            <LoanCard
              key={loan.id}
              loan={loan}
              onView={() => setDetailLoan(loan)}
              onEdit={() => openForm(loan)}
              onDelete={() => del(loan)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editLoan ? "Edit Pinjaman" : "Tambah Pinjaman Baru"}</DialogTitle>
            <DialogDescription>
              Cicilan akan otomatis dipotong dari slip gaji bulanan mulai periode yang ditentukan.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Pegawai *</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })} disabled={!!editLoan}>
                <SelectTrigger data-testid="form-user"><SelectValue placeholder="Pilih pegawai" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.employee_id})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Kategori *</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="form-category"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                <SelectContent>
                  {cats.length === 0 ? (
                    <SelectItem value="_empty" disabled>Belum ada kategori. Buat dulu di tab Kategori</SelectItem>
                  ) : cats.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Judul / Nama Pinjaman *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Contoh: Honda Beat 2026"
                data-testid="form-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Nominal Pinjaman *</Label>
                <Input
                  type="number"
                  value={form.principal}
                  onChange={(e) => setForm({ ...form, principal: e.target.value })}
                  placeholder="10000000"
                  data-testid="form-principal"
                />
              </div>
              <div>
                <Label className="text-xs">Cicilan / Bulan *</Label>
                <Input
                  type="number"
                  value={form.monthly_minimum}
                  onChange={(e) => setForm({ ...form, monthly_minimum: e.target.value })}
                  placeholder="500000"
                  data-testid="form-monthly"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Mulai Dipotong *</Label>
              <Input
                type="month"
                value={form.start_period}
                onChange={(e) => setForm({ ...form, start_period: e.target.value })}
                data-testid="form-start"
              />
              <p className="text-xs text-zinc-500 mt-1">Default mulai bulan berikutnya setelah disetujui</p>
            </div>
            <div>
              <Label className="text-xs">Deskripsi</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Keterangan tambahan (opsional)"
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenCreate(false)}>Batal</Button>
              <Button type="submit" disabled={busy} data-testid="btn-submit-loan">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editLoan ? "Update" : "Simpan")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      {detailLoan && (
        <LoanDetailDialog
          loan={detailLoan}
          onClose={() => setDetailLoan(null)}
          onChanged={() => { refresh(); setDetailLoan(null); }}
        />
      )}
    </div>
  );
};

const LoanCard = ({ loan, onView, onEdit, onDelete }) => {
  const progress = loan.principal > 0 ? Math.min(100, (loan.total_paid / loan.principal) * 100) : 0;
  return (
    <Card className="p-4 border border-zinc-200 shadow-none" data-testid={`loan-card-${loan.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge status={loan.status} />
            <Badge variant="outline" className="text-xs">{loan.category_name}</Badge>
          </div>
          <h3 className="font-display font-semibold text-base truncate">{loan.title}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            <span className="font-medium">{loan.user_name}</span> · {loan.employee_id} ·
            Mulai {dispDate(loan.start_period + "-01").replace(/-\d{2}-/, "-")}
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button size="sm" variant="ghost" onClick={onView} data-testid={`btn-view-${loan.id}`}>
            <Eye className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`btn-edit-${loan.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-600" data-testid={`btn-del-${loan.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
        <div>
          <p className="text-xs text-zinc-500">Pokok</p>
          <p className="font-mono font-semibold">{formatRupiah(loan.principal)}</p>
        </div>
        <div>
          <p className="text-xs text-zinc-500">Dibayar</p>
          <p className="font-mono font-semibold text-green-700">{formatRupiah(loan.total_paid)}</p>
        </div>
        <div>
          <p className="text-xs text-zinc-500">Sisa</p>
          <p className="font-mono font-semibold text-amber-700">{formatRupiah(loan.outstanding)}</p>
        </div>
      </div>

      <div className="w-full bg-zinc-100 rounded-full h-1.5 overflow-hidden">
        <div
          className={`h-full transition-all ${loan.is_paid_off ? "bg-green-500" : "bg-primary"}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-xs text-zinc-500 mt-1">
        {loan.months_paid} bulan terbayar · Cicilan/bln {formatRupiah(loan.monthly_minimum)}
      </p>
    </Card>
  );
};

const LoanDetailDialog = ({ loan: initial, onClose, onChanged }) => {
  const [loan, setLoan] = useState(initial);
  const [overrideForm, setOverrideForm] = useState({ period: currentYM(), amount: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  const reload = async () => {
    const r = await api.get(`/loans/${loan.id}`);
    setLoan(r.data);
  };

  const submitOverride = async (e) => {
    e.preventDefault();
    if (!overrideForm.period || overrideForm.amount === "") {
      toast.error("Periode dan nominal wajib diisi");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/loans/${loan.id}/payments`, {
        period: overrideForm.period,
        amount: parseFloat(overrideForm.amount),
        note: overrideForm.note,
      });
      toast.success("Pembayaran tersimpan");
      setOverrideForm({ period: currentYM(), amount: "", note: "" });
      await reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setBusy(false);
    }
  };

  const delOverride = async (period) => {
    if (!window.confirm(`Hapus override pembayaran bulan ${period}? Akan kembali ke cicilan default.`)) return;
    try {
      await api.delete(`/loans/${loan.id}/payments/${period}`);
      toast.success("Override dihapus");
      await reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal hapus");
    }
  };

  const changeStatus = async (newStatus) => {
    if (!window.confirm(`Ubah status menjadi "${STATUS_MAP[newStatus][1]}"?`)) return;
    setStatusBusy(true);
    try {
      await api.put(`/loans/${loan.id}`, { status: newStatus });
      toast.success("Status diperbarui");
      await reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    } finally {
      setStatusBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {loan.title}
            <StatusBadge status={loan.status} />
          </DialogTitle>
          <DialogDescription>
            {loan.user_name} ({loan.employee_id}) · {loan.category_name}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 my-3">
          <div className="bg-zinc-50 p-3 rounded-md">
            <p className="text-xs text-zinc-500">Pokok</p>
            <p className="font-mono font-bold">{formatRupiah(loan.principal)}</p>
          </div>
          <div className="bg-green-50 p-3 rounded-md">
            <p className="text-xs text-zinc-500">Dibayar</p>
            <p className="font-mono font-bold text-green-700">{formatRupiah(loan.total_paid)}</p>
          </div>
          <div className="bg-amber-50 p-3 rounded-md">
            <p className="text-xs text-zinc-500">Sisa Hutang</p>
            <p className="font-mono font-bold text-amber-700">{formatRupiah(loan.outstanding)}</p>
          </div>
        </div>

        {loan.description && (
          <div className="mb-3">
            <p className="text-xs text-zinc-500">Deskripsi</p>
            <p className="text-sm">{loan.description}</p>
          </div>
        )}

        {/* Status actions */}
        <div className="flex flex-wrap gap-2 mb-4">
          {loan.status === "active" && (
            <>
              <Button size="sm" variant="outline" onClick={() => changeStatus("paid_off")} disabled={statusBusy} data-testid="btn-mark-paid">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Tandai Lunas
              </Button>
              <Button size="sm" variant="outline" onClick={() => changeStatus("cancelled")} disabled={statusBusy} className="text-red-600" data-testid="btn-cancel-loan">
                <X className="w-3.5 h-3.5 mr-1" /> Batalkan
              </Button>
            </>
          )}
          {loan.status !== "active" && (
            <Button size="sm" variant="outline" onClick={() => changeStatus("active")} disabled={statusBusy}>
              Aktifkan kembali
            </Button>
          )}
        </div>

        {/* Schedule */}
        <h4 className="font-display text-sm font-semibold mb-2">Riwayat Cicilan</h4>
        {loan.schedule.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4 text-center bg-zinc-50 rounded-md">
            Belum ada periode terlewati. Cicilan mulai dipotong bulan {loan.start_period}.
          </p>
        ) : (
          <div className="border border-zinc-200 rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="text-xs">Periode</TableHead>
                  <TableHead className="text-xs text-right">Dipotong</TableHead>
                  <TableHead className="text-xs">Tipe</TableHead>
                  <TableHead className="text-xs text-right">Sisa Setelah</TableHead>
                  <TableHead className="text-xs">Catatan</TableHead>
                  <TableHead className="text-xs w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loan.schedule.map((s) => (
                  <TableRow key={s.period} data-testid={`schedule-${s.period}`}>
                    <TableCell className="font-mono text-xs">{s.period}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{formatRupiah(s.amount)}</TableCell>
                    <TableCell>
                      {s.is_override ? (
                        <Badge variant="outline" className="text-[0.65rem] bg-blue-50 text-blue-700 border-blue-300">Override</Badge>
                      ) : (
                        <span className="text-xs text-zinc-500">Auto</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{formatRupiah(s.balance_after)}</TableCell>
                    <TableCell className="text-xs text-zinc-600">{s.note || "-"}</TableCell>
                    <TableCell>
                      {s.is_override && (
                        <Button size="sm" variant="ghost" onClick={() => delOverride(s.period)} className="text-red-600 h-7 w-7 p-0" data-testid={`btn-del-override-${s.period}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Override form */}
        {loan.status === "active" && (
          <Card className="p-4 mt-4 border border-blue-200 bg-blue-50/30 shadow-none">
            <p className="overline mb-2">Override Bulan Tertentu</p>
            <p className="text-xs text-zinc-600 mb-3">
              Gunakan jika pegawai bayar <b>lebih</b> atau <b>kurang</b> dari cicilan default ({formatRupiah(loan.monthly_minimum)}) di bulan tertentu.
            </p>
            <form onSubmit={submitOverride} className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <div>
                <Label className="text-xs">Bulan</Label>
                <Input
                  type="month"
                  value={overrideForm.period}
                  onChange={(e) => setOverrideForm({ ...overrideForm, period: e.target.value })}
                  data-testid="override-period"
                />
              </div>
              <div>
                <Label className="text-xs">Nominal</Label>
                <Input
                  type="number"
                  value={overrideForm.amount}
                  onChange={(e) => setOverrideForm({ ...overrideForm, amount: e.target.value })}
                  placeholder="1000000"
                  data-testid="override-amount"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Catatan</Label>
                <div className="flex gap-2">
                  <Input
                    value={overrideForm.note}
                    onChange={(e) => setOverrideForm({ ...overrideForm, note: e.target.value })}
                    placeholder="Bayar lebih bulan ini"
                    data-testid="override-note"
                  />
                  <Button type="submit" disabled={busy} data-testid="btn-save-override">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
                  </Button>
                </div>
              </div>
            </form>
          </Card>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
